import * as React from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useIntl } from 'react-intl';
import { IGraphCode, IGraphResponseData, getPositionDetail } from '../GraphService';
import PositionDetail from './PositionDetail';
import PositionMap from './PositionMap';
import { TVehicleStatus } from '../../Vehicles/types';
import {
  graphCurrentPointsDataAtom,
  IGraphCurrentPointsData,
  lastClickedTimestampAtom,
  tooltipCodeAtom,
} from '../GraphState';
import { setLoaderAtom } from '../../../store/recoil/AppState';
import useAnalytics from '../../../hooks/useAnalytics';

interface IPositionDetailProps {
  vehicleId: number;
  positions: IGraphResponseData[];
}

export interface IPositionData {
  speed: number | null;
  batteryVoltage: number | null;
  rpm: number | null;
  fuelLevelPercentage: number | null;
  fuelLevelLiters: number | null;
  temperature: number | null;
  cng?: number | null;
  lpg?: number | null;
  cruise: number | null;
  tachometer: number | null;
  electricRemaining: number | null;
  electricCharge: number | null;
  electricState: number | null;
  ecoDrive: number | null;
  throttle: number | null;
  sat: number;
  electricBattery: number | null;
}

export interface IPositionInGraph {
  lat: number;
  lng: number;
  angle: number;
  mode: string;
  status: TVehicleStatus;
  timestamp: string;
  address: string;
}

export default function PositionDetails(props: IPositionDetailProps): JSX.Element {
  const { trackEvent } = useAnalytics();
  const { formatMessage: f } = useIntl();
  const setLoader = useSetRecoilState<boolean>(setLoaderAtom);
  const [graphCurrentPointsData, setGraphCurrentPointsData] = useRecoilState<
    IGraphCurrentPointsData[] | null
  >(graphCurrentPointsDataAtom);
  const tooltipCode = useRecoilValue<IGraphCode | null>(tooltipCodeAtom);
  const [lastClickedTimestamp, setLastClickedTimestamp] =
    useRecoilState<string>(lastClickedTimestampAtom);
  const [data, setData] = React.useState<IPositionData | null>(null);
  const [position, setPosition] = React.useState<IPositionInGraph | null>(null);

  const handlePositionDetail = React.useCallback(
    async (code?: string, address?: string, timestamp?: string) => {
      if (code || tooltipCode?.code) {
        setLoader(true);
        const res = await getPositionDetail(props.vehicleId, code || tooltipCode?.code || '');
        if (res) {
          if (tooltipCode?.timestamp || timestamp) {
            setLastClickedTimestamp(tooltipCode?.timestamp || timestamp || '');
          }
          setPosition({
            lat: res.gpsLat,
            lng: res.gpsLon,
            timestamp: res.createdAt,
            address: address || tooltipCode?.address || '',
            angle: res.gpsAngle,
            status: res.gpsSat === 0 ? 'NO_GPS' : res.ignitionOn ? 'DRIVING' : 'STOP',
            mode: res.isPrivate ? 'private' : 'business',
          });
        }
        if (res && res.ioValues) {
          const canData = res.ioValues?.canData;
          const electricValues = res.ioValues?.electricValues;
          setData({
            speed: res.gpsSpeed,
            batteryVoltage: canData.powerSupplyVoltage.value,
            rpm: canData.engine.rpm.value,
            fuelLevelPercentage: canData.fuel.levelPerc.value,
            fuelLevelLiters: canData.fuel.levelLiters.value,
            temperature: canData.engine.temperature.value,
            throttle: canData.throttle.value,
            cng: canData.cng?.value,
            lpg: canData.lpg?.value,
            cruise: canData.cruise.value,
            tachometer: canData.tachometer.value,
            electricBattery: electricValues?.percent.value,
            electricRemaining:
              res.rawIoValues && res.rawIoValues[304] ? res.rawIoValues[304] : null,
            electricCharge: electricValues?.charging.value,
            electricState: electricValues?.state.value,
            ecoDrive: canData.ecoDrivingValues?.score ? canData.ecoDrivingValues.score / 100 : null,
            sat: res.gpsSat,
          });
        }
        setLoader(false);
      }
    },
    [props.vehicleId, setLastClickedTimestamp, setLoader, tooltipCode]
  );

  const handleGraphClick = React.useCallback(async () => {
    handlePositionDetail();
  }, [handlePositionDetail]);

  React.useEffect(() => {
    window.addEventListener('click', handleGraphClick);
    return () => {
      window.removeEventListener('click', handleGraphClick);
    };
  });

  const getUniquePositions = () => {
    const positions: IGraphCurrentPointsData[] = [];
    let timestamp = '';
    props.positions.forEach((i) => {
      if (timestamp !== i.timestamp) {
        positions.push({
          timestamp: i.timestamp,
          code: i.code,
          address: i.address,
        });
        timestamp = i.timestamp;
      }
    });
    return positions;
  };

  const handleLeftArrow = () => {
    if (props.positions) {
      trackEvent(['Velocity graph page', 'click', 'Previous position button']);
      const positions: any = graphCurrentPointsData || getUniquePositions();
      const lastTimestamp =
        lastClickedTimestamp || props.positions[props.positions.length - 1]?.timestamp;
      const selectedPosition = positions.find(
        (i: IPositionInGraph) => i.timestamp === lastTimestamp
      );
      const selectedIndex = positions.indexOf(selectedPosition);
      const newIndex = selectedIndex - 1;
      if (newIndex >= 0) {
        const code = positions[newIndex]?.code;
        const address = positions[newIndex]?.address;
        const timestamp = positions[newIndex]?.timestamp;
        handlePositionDetail(code, address, timestamp);
      }
    }
  };

  const handleRightArrow = () => {
    if (props.positions) {
      trackEvent(['Velocity graph page', 'click', 'Next position button']);
      const positions: any = graphCurrentPointsData || getUniquePositions();
      const lastTimestamp =
        lastClickedTimestamp || props.positions[props.positions.length - 1]?.timestamp;
      const selectedPosition = positions.find(
        (i: IPositionInGraph) => i.timestamp === lastTimestamp
      );
      const selectedIndex = positions.indexOf(selectedPosition);
      const newIndex = selectedIndex + 1;
      if (newIndex < positions.length) {
        const code = positions[newIndex]?.code;
        const address = positions[newIndex]?.address;
        const timestamp = positions[newIndex]?.timestamp;
        handlePositionDetail(code, address, timestamp);
      }
    }
  };

  React.useEffect(() => {
    if (props.positions && props.positions.length > 0) {
      setGraphCurrentPointsData(null);
      const code = props.positions[props.positions.length - 1]?.code;
      const address = props.positions[props.positions.length - 1]?.address;
      handlePositionDetail(code, address);
    } else {
      setPosition(null);
      setData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.positions]);

  const getLeftArrowTitle = () => {
    if (props.positions && position) {
      let firstPositionTimestamp = '';
      if (graphCurrentPointsData) {
        firstPositionTimestamp = graphCurrentPointsData[0]?.timestamp;
      } else {
        firstPositionTimestamp = props.positions[0]?.timestamp;
      }
      const lastTimestamp = lastClickedTimestamp || props.positions[0]?.timestamp;

      if (firstPositionTimestamp === lastTimestamp) {
        return '';
      }
      return f({ id: 'graph.positionDetail.arrowLeft.title' });
    }
    return '';
  };

  const getRightArrowTitle = () => {
    if (props.positions && position) {
      let lastPositionTimestamp = '';
      if (graphCurrentPointsData) {
        lastPositionTimestamp =
          graphCurrentPointsData[graphCurrentPointsData.length - 1]?.timestamp;
      } else {
        lastPositionTimestamp = props.positions[props.positions.length - 1]?.timestamp;
      }
      const lastTimestamp =
        lastClickedTimestamp || props.positions[props.positions.length - 1]?.timestamp;

      if (lastPositionTimestamp === lastTimestamp) {
        return '';
      }
      return f({ id: 'graph.positionDetail.arrowRight.title' });
    }
    return '';
  };

  return (
    <div className="row">
      <div className="col-xxl-6">
        <PositionDetail
          data={data}
          position={position}
          handleLeftArrow={handleLeftArrow}
          handleRightArrow={handleRightArrow}
          leftArrowTitle={getLeftArrowTitle()}
          rightArrowTitle={getRightArrowTitle()}
        />
      </div>
      <div className="col-xxl-6">
        <PositionMap position={position} vehicleId={props.vehicleId} />
      </div>
    </div>
  );
}
